/**
 * EditDocumentChatFlow — single-document edit flow from the document checklist.
 * Chat: current choice → want to change? (Yes/No) → if Yes: select method → process → thank you → Continue to checklist.
 * Cancel procedure at top returns to checklist.
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
  postChatFlowDocumentChoice,
  postChatFlowDocumentUpload,
  type ChatFlowDocumentItem,
  type VariousCopyUploadMode,
} from '../../../../services/chatFlowApi';
import { allowsVariousCopySkip, requiresBothSidesUpload, requiresOriginalToHeadOffice } from '../../../../catalog/variousCopyDocCodes';
import { API_BASE_URL } from '../../../../config';
import { getEmployeeEmail } from '../../auth/employeeAuthStorage';
import robotAvatar from '../../../../assests/icons/robot.png';
import attachIcon from '../../../../assests/icons/attach.png';
import sendIcon from '../../../../assests/icons/send.png';
import cautionIcon from '../../../../assests/icons/caution.png';
import ContinueBlock from '../components/ContinueBlock';
import PdfCanvasViewer from '../../documents/pdfPreview/PdfCanvasViewer';
import CardCameraCapture, { type CardCameraSide } from './CardCameraCapture';
import './ChatDocumentFlow.css';

export interface EditDocumentChatFlowProps {
  doc: ChatFlowDocumentItem;
  invitationId: string;
  /** Cancel procedure → back to document checklist */
  onBack: () => void;
  /** Continue to document checklist (after update or no change) */
  onComplete: () => void;
}

type EditStep =
  | 'intro'
  | 'no_change'
  | 'select_method'
  | 'upload'
  | 'updated';

type PreviewKind = 'image' | 'pdf' | 'unknown';

interface PreviewState {
  url: string;
  kind: PreviewKind;
  label: string;
  /** True for blob: URLs created from a newly selected File */
  revokeOnClose: boolean;
}

const LABEL_KEYS: Record<string, string> = {
  submit_this_time: 'submitThisTime',
  send_later: 'sendLater',
  bring_when_boarding: 'bringWhenBoarding',
  skipped: 'doNotHaveDocument',
};

function getCurrentChoiceKey(status: string): string {
  if (status === 'send_later' || status === 'bring_when_boarding' || status === 'skipped') {
    return status;
  }
  return 'submit_this_time';
}

function previewKindFromPath(path: string | null | undefined): PreviewKind {
  if (!path) return 'unknown';
  const lower = path.toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.png') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.webp')
  ) {
    return 'image';
  }
  return 'unknown';
}

function previewKindFromFile(file: File): PreviewKind {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'pdf';
  return previewKindFromPath(file.name);
}

function fileNameFromPath(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || path;
}

function buildUploadedFileUrl(
  email: string,
  invitationId: string,
  docCode: string,
  side?: 'back'
): string | null {
  if (!API_BASE_URL || !email.trim() || !invitationId.trim() || !docCode.trim()) return null;
  const params = new URLSearchParams({
    email: email.trim(),
    invitationId: invitationId.trim(),
    docCode: docCode.trim(),
  });
  if (side === 'back') params.set('side', 'back');
  return `${API_BASE_URL}/api/chat-flow/documents/file?${params.toString()}`;
}

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function resolveUploadedFilePath(doc: ChatFlowDocumentItem): string | null {
  if (doc.filePath?.trim()) return doc.filePath.trim();
  const rounds = doc.filesByRound ?? [];
  const withFile = [...rounds]
    .filter((r) => r.filePath?.trim())
    .sort((a, b) => (b.submissionRound ?? 0) - (a.submissionRound ?? 0));
  return withFile[0]?.filePath?.trim() ?? null;
}

function resolveUploadedFilePathBack(doc: ChatFlowDocumentItem): string | null {
  if (doc.filePathBack?.trim()) return doc.filePathBack.trim();
  const rounds = doc.filesByRound ?? [];
  const withFile = [...rounds]
    .filter((r) => r.filePathBack?.trim())
    .sort((a, b) => (b.submissionRound ?? 0) - (a.submissionRound ?? 0));
  return withFile[0]?.filePathBack?.trim() ?? null;
}

function AssistantRow({
  children,
  animateIn = true,
}: {
  children: React.ReactNode;
  animateIn?: boolean;
}) {
  return (
    <div className={`chat-document-flow__row ${animateIn ? 'chat-document-flow__row--enter' : ''}`}>
      <div className="chat-document-flow__avatar-wrap" aria-hidden>
        <img src={robotAvatar} alt="" className="chat-document-flow__avatar" />
      </div>
      <div className="chat-document-flow__bubble-wrap chat-document-flow__bubble-wrap--grey">
        <div className="chat-document-flow__bubble chat-document-flow__bubble--grey">{children}</div>
      </div>
    </div>
  );
}

const EditDocumentChatFlow: React.FC<EditDocumentChatFlowProps> = ({
  doc,
  invitationId,
  onBack,
  onComplete,
}) => {
  const { t, language } = useLanguage();
  const freshSubmission = doc.choiceRecorded === false;
  const [step, setStep] = useState<EditStep>(freshSubmission ? 'select_method' : 'intro');
  const [userYesNo, setUserYesNo] = useState<boolean | null>(null);
  const [choiceBusy, setChoiceBusy] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileBack, setSelectedFileBack] = useState<File | null>(null);
  const [uploadMode, setUploadMode] = useState<VariousCopyUploadMode | null>(null);
  const [showUploadModeModal, setShowUploadModeModal] = useState(false);
  const [attachTarget, setAttachTarget] = useState<'combined' | 'front' | 'back'>('combined');
  const [cameraSide, setCameraSide] = useState<CardCameraSide | null>(null);
  const [showUploadConfirmModal, setShowUploadConfirmModal] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const previewRef = useRef<PreviewState | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [updatedViaOriginalUpload, setUpdatedViaOriginalUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);

  const rawDocName =
    language === 'ja'
      ? (doc.nameJa || doc.name || doc.docCode)
      : (doc.name || doc.nameJa || doc.docCode);
  let docName = rawDocName || t('submitDocuments.chat.documentPrefix');
  if (language === 'ja') {
    docName = docName.replace(/\(/g, '（').replace(/\)/g, '）');
  }
  const currentChoiceKey = getCurrentChoiceKey(doc.status);
  const currentChoiceLabel =
    t(`submitDocuments.chat.${LABEL_KEYS[currentChoiceKey]}`) || currentChoiceKey;

  const editMsg = (key: string) => t(`submitDocuments.edit.${key}`) || key;
  const chatMsg = (key: string) => t(`submitDocuments.chat.${key}`) || key;

  const currentSubmissionText = editMsg('currentSubmission')
    .replace(/\{\{docName\}\}/g, docName)
    .replace(/\{\{choice\}\}/g, currentChoiceLabel);

  const uploadedFilePath = useMemo(() => resolveUploadedFilePath(doc), [doc]);
  const uploadedFilePathBack = useMemo(() => resolveUploadedFilePathBack(doc), [doc]);
  const canPreviewUploaded = Boolean(uploadedFilePath);
  const canPreviewUploadedBack = Boolean(uploadedFilePathBack);
  const needsBothSides = doc.requiresBothSides === true || requiresBothSidesUpload(doc.docCode);

  const resetUploadSelection = useCallback(() => {
    setSelectedFile(null);
    setSelectedFileBack(null);
    setUploadMode(null);
    setShowUploadModeModal(false);
    setAttachTarget('combined');
    setCameraSide(null);
  }, []);

  const openNativeFilePicker = useCallback((target: 'combined' | 'front' | 'back', acceptImagesOnly: boolean) => {
    const input = fileInputRef.current;
    if (!input) return;
    input.accept = acceptImagesOnly ? 'image/*' : '.pdf,image/*';
    input.removeAttribute('capture');
    setAttachTarget(target);
    input.value = '';
    input.click();
  }, []);

  const openFilePicker = useCallback(
    (target: 'combined' | 'front' | 'back', mode: VariousCopyUploadMode | null) => {
      if (mode === 'camera' && (target === 'front' || target === 'back')) {
        setAttachTarget(target);
        setCameraSide(target);
        return;
      }
      openNativeFilePicker(target, false);
    },
    [openNativeFilePicker]
  );

  const handleAttachClick = useCallback(() => {
    if (!needsBothSides) {
      openFilePicker('combined', null);
      return;
    }
    if (!uploadMode) {
      setShowUploadModeModal(true);
      return;
    }
    if (uploadMode === 'combined') {
      openFilePicker('combined', 'combined');
      return;
    }
    if (!selectedFile) {
      openFilePicker('front', uploadMode);
    } else if (!selectedFileBack) {
      openFilePicker('back', uploadMode);
    } else {
      openFilePicker('front', uploadMode);
    }
  }, [needsBothSides, uploadMode, selectedFile, selectedFileBack, openFilePicker]);

  const handleSelectUploadMode = (mode: VariousCopyUploadMode) => {
    setUploadMode(mode);
    setShowUploadModeModal(false);
    if (mode === 'combined') {
      openFilePicker('combined', mode);
    } else if (mode === 'camera') {
      openFilePicker('front', mode);
    }
  };

  const handleCameraCaptured = (file: File) => {
    const side = cameraSide;
    setCameraSide(null);
    if (side === 'back') {
      setSelectedFileBack(file);
    } else {
      setSelectedFile(file);
      if (!selectedFileBack) {
        setTimeout(() => setCameraSide('back'), 200);
      }
    }
  };

  const scrollToBottom = useCallback(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const closePreview = useCallback(() => {
    setShowFilePreview(false);
    setPreview((prev) => {
      if (prev?.revokeOnClose) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  useEffect(() => {
    return () => {
      const current = previewRef.current;
      if (current?.revokeOnClose) URL.revokeObjectURL(current.url);
    };
  }, []);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (attachTarget === 'back') {
      setSelectedFileBack(file);
    } else {
      setSelectedFile(file);
    }
  };

  const canSubmitUpload = (): boolean => {
    if (!selectedFile) return false;
    const separate = needsBothSides && (uploadMode === 'separate' || uploadMode === 'camera');
    if (separate) return Boolean(selectedFileBack);
    return true;
  };

  const handleSendUpload = () => {
    if (!canSubmitUpload()) {
      if (needsBothSides && (uploadMode === 'separate' || uploadMode === 'camera')) {
        if (!selectedFile) openFilePicker('front', uploadMode);
        else if (!selectedFileBack) openFilePicker('back', uploadMode);
      } else {
        handleAttachClick();
      }
      return;
    }
    setShowUploadConfirmModal(true);
  };

  const handleOpenLocalPreview = (file?: File | null) => {
    const target = file ?? selectedFile;
    if (!target) return;
    closePreview();
    const url = URL.createObjectURL(target);
    setPreview({
      url,
      kind: previewKindFromFile(target),
      label: target.name,
      revokeOnClose: true,
    });
    setShowFilePreview(true);
  };

  const handleOpenUploadedPreview = (side?: 'back') => {
    const email = getEmployeeEmail();
    const path = side === 'back' ? uploadedFilePathBack : uploadedFilePath;
    if (!email || !path) return;
    const url = buildUploadedFileUrl(email, invitationId, doc.docCode, side);
    if (!url) return;
    closePreview();
    setPreview({
      url,
      kind: previewKindFromPath(path),
      label: fileNameFromPath(path),
      revokeOnClose: false,
    });
    setShowFilePreview(true);
  };

  const handleConfirmUpload = async () => {
    const email = getEmployeeEmail();
    if (!email || !selectedFile) return;
    setUploadError(null);
    closePreview();
    setChoiceBusy(true);
    const separate = needsBothSides && (uploadMode === 'separate' || uploadMode === 'camera');
    const result = await postChatFlowDocumentUpload(
      email,
      invitationId,
      doc.docCode,
      selectedFile,
      separate && selectedFileBack
        ? { fileBack: selectedFileBack, uploadMode: uploadMode ?? 'separate' }
        : uploadMode
          ? { uploadMode }
          : undefined
    );
    setChoiceBusy(false);
    if (result.ok) {
      resetUploadSelection();
      setShowUploadConfirmModal(false);
      setUpdatedViaOriginalUpload(requiresOriginalToHeadOffice(doc.docCode));
      setStep('updated');
      setTimeout(scrollToBottom, 100);
    } else {
      const errKey =
        result.error && t(`submitDocuments.chat.uploadError_${result.error}`) !== `submitDocuments.chat.uploadError_${result.error}`
          ? t(`submitDocuments.chat.uploadError_${result.error}`)
          : t('submitDocuments.chat.uploadError');
      setUploadError(errKey);
    }
  };

  const handleChoice = async (status: 'send_later' | 'bring_when_boarding' | 'notyet' | 'skipped') => {
    const email = getEmployeeEmail();
    if (!email || choiceBusy) return;
    setChoiceBusy(true);
    const ok = await postChatFlowDocumentChoice(email, invitationId, doc.docCode, status);
    setChoiceBusy(false);
    if (ok) {
      setUpdatedViaOriginalUpload(false);
      setStep('updated');
      setTimeout(scrollToBottom, 100);
    }
  };

  return (
    <div className="chat-document-flow">
      <div className="chat-document-flow__card">
        <header className="chat-document-flow__top">
          <button type="button" className="chat-document-flow__cancel-pill" onClick={onBack}>
            <span className="chat-document-flow__cancel-x" aria-hidden>×</span>
            {chatMsg('cancelProcedure')}
          </button>
        </header>

        <div className="chat-document-flow__thread-wrap">
          <div className="chat-document-flow__thread" role="log" aria-live="polite">
            {freshSubmission ? (
              <>
                <AssistantRow>
                  <p style={{ margin: 0 }}>{docName}</p>
                  <p style={{ margin: '8px 0 0 0' }}>{chatMsg('selectMethod')}</p>
                </AssistantRow>
                {(doc.description || doc.descriptionJa) && (
                  <AssistantRow>
                    <p className="chat-document-flow__doc-description" style={{ margin: 0 }}>
                      {doc.description || doc.descriptionJa}
                    </p>
                  </AssistantRow>
                )}
              </>
            ) : (
            <AssistantRow>
              <p style={{ margin: 0 }}>{currentSubmissionText}</p>
              {canPreviewUploaded && (
                <p style={{ margin: '8px 0 0 0' }}>
                  <button
                    type="button"
                    className="chat-document-flow__link chat-document-flow__link-btn"
                    disabled={choiceBusy}
                    onClick={() => handleOpenUploadedPreview()}
                  >
                    {editMsg('previewUploaded')}
                    {canPreviewUploadedBack ? ` (${chatMsg('uploadFrontSide')})` : ''}
                  </button>
                  {canPreviewUploadedBack && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="chat-document-flow__link chat-document-flow__link-btn"
                        disabled={choiceBusy}
                        onClick={() => handleOpenUploadedPreview('back')}
                      >
                        {chatMsg('uploadBackSide')}
                      </button>
                    </>
                  )}
                </p>
              )}
              <p style={{ margin: '8px 0 0 0' }}>{editMsg('wantToChange')}</p>
            </AssistantRow>
            )}

            {/* User's Yes/No reply once they've chosen */}
            {userYesNo !== null && (
              <div className="chat-document-flow__row chat-document-flow__row--user chat-document-flow__row--enter">
                <div className="chat-document-flow__user-bubble-wrap">
                  <div className="chat-document-flow__bubble chat-document-flow__bubble--user">
                    {userYesNo ? editMsg('yes') : editMsg('no')}
                  </div>
                </div>
              </div>
            )}

            {/* No change: system reply */}
            {step === 'no_change' && (
              <AssistantRow>{editMsg('noChanges')}</AssistantRow>
            )}

            {/* Select method (after Yes) */}
            {(step === 'select_method' || step === 'upload') && userYesNo === true && (
              <AssistantRow>
                <p style={{ margin: 0 }}>{editMsg('selectHowToUpdate')}</p>
              </AssistantRow>
            )}

            {/* Thank you after update */}
            {step === 'updated' && (
              <AssistantRow>
                {updatedViaOriginalUpload
                  ? editMsg('thankYouUpdatedOriginal')
                  : editMsg('thankYouUpdated')}
              </AssistantRow>
            )}

            <div ref={threadEndRef} aria-hidden />
          </div>
        </div>

        {/* Footer: Yes/No when intro, 3 options when select_method, upload bar when upload, continue when no_change or updated */}
        <footer className="chat-document-flow__footer chat-document-flow__footer--enter">
          <div className="chat-document-flow__answers-block">
            {step === 'intro' && (
              <>
                <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  disabled={choiceBusy}
                  onClick={() => {
                    setUserYesNo(true);
                    setStep('select_method');
                    setTimeout(scrollToBottom, 100);
                  }}
                >
                  {editMsg('yes')}
                </button>
                <hr className="chat-document-flow__answer-sep" aria-hidden />
                <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  disabled={choiceBusy}
                  onClick={() => {
                    setUserYesNo(false);
                    setStep('no_change');
                    setTimeout(scrollToBottom, 100);
                  }}
                >
                  {editMsg('no')}
                </button>
              </>
            )}

            {step === 'select_method' && (
              <>
                <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  disabled={choiceBusy}
                  onClick={() => {
                    resetUploadSelection();
                    setStep('upload');
                    if (needsBothSides) {
                      setShowUploadModeModal(true);
                    }
                  }}
                >
                  {chatMsg('submitThisTime')}
                </button>
                <hr className="chat-document-flow__answer-sep" aria-hidden />
                <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  disabled={choiceBusy}
                  onClick={() => handleChoice('send_later')}
                >
                  {chatMsg('sendLater')}
                </button>
                <hr className="chat-document-flow__answer-sep" aria-hidden />
                <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  disabled={choiceBusy}
                  onClick={() => handleChoice('bring_when_boarding')}
                >
                  {chatMsg('bringWhenBoarding')}
                </button>
                {allowsVariousCopySkip(doc.docCode) && (
                  <>
                    <hr className="chat-document-flow__answer-sep" aria-hidden />
                    <button
                      type="button"
                      className="chat-document-flow__answer-row"
                      disabled={choiceBusy}
                      onClick={() => handleChoice('skipped')}
                    >
                      {chatMsg('doNotHaveDocument')}
                    </button>
                  </>
                )}
              </>
            )}

            {step === 'upload' && (
              <div className="chat-document-flow__upload-bar">
                {needsBothSides && (uploadMode === 'separate' || uploadMode === 'camera') ? (
                  <>
                    <p className="chat-document-flow__dual-upload-hint">
                      {chatMsg('dualUploadSeparateHint')}
                    </p>
                    <div className="chat-document-flow__dual-upload-slots">
                      <div className="chat-document-flow__dual-upload-slot">
                        <span className="chat-document-flow__dual-upload-slot-label">
                          {chatMsg('uploadFrontSide')}
                        </span>
                        <div className="chat-document-flow__upload-bar-inner">
                          <span className="chat-document-flow__upload-bar-placeholder">
                            {selectedFile?.name ?? chatMsg('uploadFrontPlaceholder')}
                          </span>
                          <button
                            type="button"
                            className="chat-document-flow__upload-bar-attach"
                            onClick={() => openFilePicker('front', uploadMode)}
                            disabled={choiceBusy}
                            aria-label={chatMsg('attachFile')}
                          >
                            <img src={attachIcon} alt="" aria-hidden />
                          </button>
                        </div>
                      </div>
                      <div className="chat-document-flow__dual-upload-slot">
                        <span className="chat-document-flow__dual-upload-slot-label">
                          {chatMsg('uploadBackSide')}
                        </span>
                        <div className="chat-document-flow__upload-bar-inner">
                          <span className="chat-document-flow__upload-bar-placeholder">
                            {selectedFileBack?.name ?? chatMsg('uploadBackPlaceholder')}
                          </span>
                          <button
                            type="button"
                            className="chat-document-flow__upload-bar-attach"
                            onClick={() => openFilePicker('back', uploadMode)}
                            disabled={choiceBusy}
                            aria-label={chatMsg('attachFile')}
                          >
                            <img src={attachIcon} alt="" aria-hidden />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="chat-document-flow__upload-bar-row chat-document-flow__upload-bar-row--actions">
                      <button
                        type="button"
                        className="chat-document-flow__upload-bar-send"
                        onClick={handleSendUpload}
                        disabled={choiceBusy}
                        aria-label={chatMsg('sendUpload')}
                      >
                        <img src={sendIcon} alt="" aria-hidden />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="chat-document-flow__upload-bar-row">
                    <div className="chat-document-flow__upload-bar-inner">
                      <span className="chat-document-flow__upload-bar-placeholder">
                        {selectedFile
                          ? selectedFile.name
                          : `${docName} ${chatMsg('uploadPlaceholderSuffix')}`}
                      </span>
                      <button
                        type="button"
                        className="chat-document-flow__upload-bar-attach"
                        onClick={handleAttachClick}
                        disabled={choiceBusy}
                        aria-label={chatMsg('attachFile')}
                      >
                        <img src={attachIcon} alt="" aria-hidden />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="chat-document-flow__upload-bar-send"
                      onClick={handleSendUpload}
                      disabled={choiceBusy}
                      aria-label={chatMsg('sendUpload')}
                    >
                      <img src={sendIcon} alt="" aria-hidden />
                    </button>
                  </div>
                )}
                {needsBothSides && uploadMode && (
                  <button
                    type="button"
                    className="chat-document-flow__upload-bar-change-mode"
                    onClick={() => {
                      setSelectedFile(null);
                      setSelectedFileBack(null);
                      setUploadMode(null);
                      setShowUploadModeModal(true);
                    }}
                    disabled={choiceBusy}
                  >
                    {chatMsg('uploadModeTitle')}
                  </button>
                )}
                <button
                  type="button"
                  className="chat-document-flow__upload-bar-cancel"
                  onClick={() => {
                    setStep('select_method');
                    resetUploadSelection();
                  }}
                  disabled={choiceBusy}
                >
                  {chatMsg('cancelUploadOption')}
                </button>
              </div>
            )}

            {(step === 'no_change' || step === 'updated') && (
              <div className="chat-document-flow__continue-block-wrap">
              <ContinueBlock
                line1={editMsg('proceedToChecklist')}
                buttonLabel={editMsg('proceedToChecklist')}
                onContinue={onComplete}
              />
              </div>
            )}
          </div>
        </footer>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,image/*"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
        aria-hidden
      />

      {showUploadModeModal && step === 'upload' && (
        <div
          className="chat-document-flow__confirm-overlay"
          onClick={() => setShowUploadModeModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-upload-mode-title"
        >
          <div
            className="chat-document-flow__confirm-modal chat-document-flow__mode-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chat-document-flow__confirm-header">
              <h2 id="edit-upload-mode-title" className="chat-document-flow__confirm-title">
                {chatMsg('uploadModeTitle')}
              </h2>
            </div>
            <div className="chat-document-flow__confirm-body">
              <p className="chat-document-flow__confirm-message">{chatMsg('uploadModeHint')}</p>
              <div className="chat-document-flow__mode-options">
                <button
                  type="button"
                  className="chat-document-flow__mode-option"
                  onClick={() => handleSelectUploadMode('combined')}
                >
                  {chatMsg('uploadModeCombined')}
                </button>
                <button
                  type="button"
                  className="chat-document-flow__mode-option"
                  onClick={() => handleSelectUploadMode('separate')}
                >
                  {chatMsg('uploadModeSeparate')}
                </button>
                {isMobileDevice() && (
                  <button
                    type="button"
                    className="chat-document-flow__mode-option"
                    onClick={() => handleSelectUploadMode('camera')}
                  >
                    {chatMsg('uploadModeCamera')}
                  </button>
                )}
              </div>
            </div>
            <div className="chat-document-flow__confirm-actions">
              <button
                type="button"
                className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--cancel"
                onClick={() => setShowUploadModeModal(false)}
              >
                {chatMsg('confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload confirmation modal */}
      {showUploadConfirmModal && selectedFile && (
        <div
          className="chat-document-flow__confirm-overlay"
          onClick={() => {
            if (!choiceBusy) {
              setShowUploadConfirmModal(false);
              setUploadError(null);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-upload-confirm-title"
        >
          <div
            className="chat-document-flow__confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chat-document-flow__confirm-header">
              <h2 id="edit-upload-confirm-title" className="chat-document-flow__confirm-title">
                {chatMsg('confirmTitle')}
              </h2>
              <img src={cautionIcon} alt="" className="chat-document-flow__confirm-icon" aria-hidden />
            </div>
            <div className="chat-document-flow__confirm-body">
              {uploadError && (
                <p className="chat-document-flow__confirm-error" role="alert">
                  {uploadError}
                </p>
              )}
              <p className="chat-document-flow__confirm-message">
                {docName} {chatMsg('confirmMessageSuffix')}
              </p>
              <p className="chat-document-flow__confirm-hint">{chatMsg('confirmReuploadHint')}</p>
              <div className="chat-document-flow__confirm-file-row">
                <span className="chat-document-flow__confirm-filename">{selectedFile.name}</span>
                <button
                  type="button"
                  className="chat-document-flow__confirm-preview"
                  onClick={() => handleOpenLocalPreview(selectedFile)}
                  disabled={choiceBusy}
                  aria-label={chatMsg('preview')}
                >
                  <span className="chat-document-flow__confirm-preview-icon" aria-hidden />
                  {chatMsg('preview')}
                </button>
              </div>
              {selectedFileBack && (
                <div className="chat-document-flow__confirm-file-row">
                  <span className="chat-document-flow__confirm-filename">
                    {chatMsg('uploadBackSide')}: {selectedFileBack.name}
                  </span>
                  <button
                    type="button"
                    className="chat-document-flow__confirm-preview"
                    onClick={() => handleOpenLocalPreview(selectedFileBack)}
                    disabled={choiceBusy}
                    aria-label={chatMsg('preview')}
                  >
                    <span className="chat-document-flow__confirm-preview-icon" aria-hidden />
                    {chatMsg('preview')}
                  </button>
                </div>
              )}
            </div>
            <div className="chat-document-flow__confirm-actions">
              <button
                type="button"
                className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--cancel"
                onClick={() => {
                  setShowUploadConfirmModal(false);
                  setUploadError(null);
                }}
                disabled={choiceBusy}
              >
                {chatMsg('confirmCancel')}
              </button>
              <span className="chat-document-flow__confirm-actions-sep" aria-hidden />
              <button
                type="button"
                className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--upload"
                onClick={handleConfirmUpload}
                disabled={choiceBusy}
              >
                {chatMsg('confirmUpload')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFilePreview && preview && (
        <div
          className="chat-document-flow__preview-overlay"
          onClick={closePreview}
          role="dialog"
          aria-modal="true"
          aria-label={chatMsg('preview')}
        >
          <div
            className="chat-document-flow__preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="chat-document-flow__preview-close"
              onClick={closePreview}
              aria-label={chatMsg('closeInstruction')}
            >
              ×
            </button>
            <div className="chat-document-flow__preview-body">
              {preview.kind === 'image' ? (
                <img
                  src={preview.url}
                  alt={preview.label}
                  className="chat-document-flow__preview-img"
                />
              ) : preview.kind === 'pdf' ? (
                <PdfCanvasViewer
                  pdfUrl={preview.url}
                  title={preview.label}
                  className="chat-document-flow__preview-pdf"
                />
              ) : (
                <p className="chat-document-flow__preview-fallback">
                  {chatMsg('previewNotAvailable')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {cameraSide && (
        <CardCameraCapture
          side={cameraSide}
          sideLabel={
            cameraSide === 'back' ? chatMsg('uploadBackSide') : chatMsg('uploadFrontSide')
          }
          tipText={chatMsg('cameraTip')}
          captureLabel={chatMsg('cameraCapture')}
          usePhotoLabel={chatMsg('cameraUsePhoto')}
          retakeLabel={chatMsg('cameraRetake')}
          cancelLabel={chatMsg('cameraCancel')}
          errorPermissionLabel={chatMsg('cameraErrorPermission')}
          errorGenericLabel={chatMsg('cameraErrorGeneric')}
          fallbackLabel={chatMsg('cameraFallback')}
          onCaptured={handleCameraCaptured}
          onCancel={() => setCameraSide(null)}
          onFallback={() => {
            const target = cameraSide;
            setCameraSide(null);
            openNativeFilePicker(target, true);
          }}
        />
      )}
    </div>
  );
};

export default EditDocumentChatFlow;
